import { ProgramProps } from '../../pipeline-base';
import { PipelineGlUtils } from '../../utils/pipeline-utils-gl';

import { maskRegionGlslVec3PropString } from './regionMaskTypes';

/**
 * This class is created to try using components with pipelines, in lieu of making entirely new pipelines when a few small variations of what already exists,
 * need to be done.
 */
export class RegionMask {
  /**
   * The base texture will get this reference so as to presume and allow for uSamplers[0] to be modified and updated throughout the draw-series process.
   * Perhaps rather than this, the draw series class needs to simply hold an "updated texture" reference... will refactor if needed, once it's all working.
   */
  public glTextureBaseRef?: WebGLTexture;
  public glTextureMask?: WebGLTexture;
  public scaleDownResult: boolean;

  public constructor() {
    this.scaleDownResult = false;
  }

  public static ModifyFragShaderText(fragShader: string, colourName: string): string {
    // console.trace(`Modifying fragment shader to incorporate region mask. Original shader:`, fragShader);

    const colourProp = maskRegionGlslVec3PropString[colourName];

    // Add uniforms fields
    const fieldStr = `
uniform sampler2D uBaseTexture;
uniform sampler2D uRegionMask;
uniform bool uScaleDownResult;
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskUniforms}}/gi, fieldStr);

    // Add uniform check to body
    const usageStr = `
  vec4 maskTexColour = texture2D(uRegionMask, outTexCoord);

  // MASK GRADIENT ACCEPTANCE

  if(maskTexColour.${colourProp} == 0.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
    return;
  }

  // Set texColourToShade to the base colours (red-scale to be altered) to make sure the proper result is achieved.
  texColourToShade = texture2D(uBaseTexture, outTexCoord);
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskCheck}}/gi, usageStr);

    // "gl_FragColor" has most up-to-date colour after masking.
    const shadeStr = `
  // When the mask pixel is < 1, we want to scale back each of the values of the given colour, and add that result to whatever the output texture has in place.
  if(uScaleDownResult && maskTexColour.${colourProp} < 1.0) {
    vec4 maskColourChannelModdedOutput = gl_FragColor * maskTexColour.${colourProp};
    gl_FragColor = vec4(maskColourChannelModdedOutput);
  }
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskShade}}/gi, shadeStr);

    // * Remove comments
    // fragShader = fragShader.replace(/\/\*[\s\S]*?\*\/|(?<=[^:])\/\/.*|^\/\/.*/g,'');
    // The positive lookbehind (?<= ) syntax in the regular expression is not supported for ios version below 16.4 which will cause crash.

    // * Remove block comments
    fragShader = fragShader.replace(/\/\*[\s\S]*?\*\//g, '');

    // * Remove line comments that do not follow a colon (to avoid URLs)
    fragShader = fragShader.replace(/([^:]|^)\/\/.*$/gm, '$1');

    return fragShader;
  }

  public CreateSetTexture(gl: WebGLRenderingContext, imgData: ImageData) {
    this.glTextureMask = PipelineGlUtils.CreateGlTextureByImageData(gl, imgData, gl.CLAMP_TO_EDGE);
  }

  public SetTextures(textureBaseRef: WebGLTexture, textureMask: WebGLTexture) {
    this.glTextureBaseRef = textureBaseRef;
    this.glTextureMask = textureMask;
  }

  public UpdateBaseTexture(textureBaseRef: WebGLTexture) {
    this.glTextureBaseRef = textureBaseRef;
  }

  public RemoveTexture() {
    // console.log(`Region Mask remove texture`);
    this.glTextureMask = undefined;
  }

  public GetProgramProps(gl: WebGLRenderingContext, shaderProgramProps: ProgramProps, shaderProgram: WebGLProgram) {
    shaderProgramProps.u_baseTexture = PipelineGlUtils.GetUniformLocation(gl, shaderProgram, 'uBaseTexture');
    shaderProgramProps.u_regionMask = PipelineGlUtils.GetUniformLocation(gl, shaderProgram, 'uRegionMask');
    shaderProgramProps.u_scaleDownResult = PipelineGlUtils.GetUniformLocation(gl, shaderProgram, 'uScaleDownResult');
  }

  public SetUniforms(gl: WebGLRenderingContext, nextTextureIndex: number, shaderProgramProps: ProgramProps) {
    // console.log(`In RegionMask SetUniforms: has texture <${!!this.glTextureMask}>, and will look for pixels of colour:`, this.colourAccepted);

    if (!this.glTextureBaseRef || !this.glTextureMask) { return; }

    // ? index...?
    PipelineGlUtils.SetTextureUniform(gl, gl.TEXTURE0 + nextTextureIndex, this.glTextureBaseRef, shaderProgramProps.u_baseTexture, nextTextureIndex);
    PipelineGlUtils.SetTextureUniform(gl, gl.TEXTURE0 + nextTextureIndex + 1, this.glTextureMask, shaderProgramProps.u_regionMask, nextTextureIndex + 1);
    gl.uniform1i(shaderProgramProps.u_scaleDownResult, Number(this.scaleDownResult));
  }
}
